import { DAY_START_DEFAULT, nextDayStart } from '@selflevel/domain';
import { db } from './db';
import { updateSettings } from './mutations';
import { zonaDelDispositivo } from './session';

/**
 * EL RELOJ DE «HOY».
 *
 * `useToday` calculaba el día en cada render, y nada volvía a pintar la app al
 * cruzar el inicio del día ni al volver de segundo plano. Una PWA instalada se
 * queda abierta días enteros: por la mañana Hoy seguía enseñando ayer, y lo que se
 * marcaba caía en ayer.
 *
 * Esto avisa a quien escuche en tres momentos:
 *  · cuando empieza el día lógico siguiente —un temporizador a esa hora exacta, con
 *    un tope de una hora porque los temporizadores se duermen con el dispositivo—;
 *  · al volver a la pestaña (`visibilitychange`);
 *  · al recuperar el foco.
 *
 * No guarda la fecha: quien escucha la recalcula y React solo vuelve a pintar si
 * cambió. Y en cada aviso se aprovecha para poner `settings.tz` al día con la zona
 * del dispositivo, que es la que usa el servidor para los avisos push.
 */

const oyentes = new Set<() => void>();
let inicioDelDia = DAY_START_DEFAULT;
let temporizador: ReturnType<typeof setTimeout> | null = null;

/** Un temporizador largo se retrasa al suspender el dispositivo: se mira cada hora. */
const TOPE_MS = 60 * 60_000;

function despertar(): void {
  for (const oyente of oyentes) oyente();
  programar();
  void sincronizarZona();
}

function programar(): void {
  if (temporizador) clearTimeout(temporizador);
  temporizador = null;
  if (oyentes.size === 0) return;
  const ahora = new Date();
  const siguiente = nextDayStart(ahora, zonaDelDispositivo(), inicioDelDia);
  // Un segundo de margen para mirar ya dentro del día nuevo.
  const ms = siguiente.getTime() - ahora.getTime() + 1_000;
  temporizador = setTimeout(despertar, Math.min(Math.max(ms, 1_000), TOPE_MS));
}

function alCambiarVisibilidad(): void {
  if (document.visibilityState === 'visible') despertar();
}

/** Para `useSyncExternalStore`. Escuchar arranca el reloj; dejar de escuchar lo para. */
export function suscribirReloj(oyente: () => void): () => void {
  oyentes.add(oyente);
  if (oyentes.size === 1) {
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    window.addEventListener('focus', despertar);
    programar();
    void sincronizarZona();
  }
  return () => {
    oyentes.delete(oyente);
    if (oyentes.size > 0) return;
    document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    window.removeEventListener('focus', despertar);
    if (temporizador) clearTimeout(temporizador);
    temporizador = null;
  };
}

/** «Mi día empieza a las…» con el que se calcula la próxima cita. */
export function fijarInicioDelDia(minutos: number): void {
  if (minutos === inicioDelDia) return;
  inicioDelDia = minutos;
  programar();
}

let sincronizando: Promise<void> | null = null;

/**
 * Pone `settings.tz` igual a la zona del dispositivo si ha cambiado (un viaje).
 * Pasa por `updateSettings`, que lo marca para subir: el servidor programa los
 * avisos push con esta zona. Antes del alta no hay ajustes y no hace nada.
 */
export function sincronizarZona(): Promise<void> {
  // El foco y la visibilidad llegan juntos: una sola escritura, no dos.
  sincronizando ??= ponerZona()
    .catch((error: unknown) => console.error('[reloj]', error))
    .finally(() => {
      sincronizando = null;
    });
  return sincronizando;
}

async function ponerZona(): Promise<void> {
  const zona = zonaDelDispositivo('');
  if (!zona) return;
  const ajustes = await db.settings.get('unica');
  if (!ajustes || ajustes.tz === zona) return;
  await updateSettings({ tz: zona });
}
